import BaseLayout from '@/layout/BaseLayout';
import type { UserDto } from '@shelflix/trpc/routes/user/list/useCase';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  scrollToError,
} from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@shelflix/ui-web/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { useForm } from 'react-hook-form';

import { GenericSelect } from '@/components/GenericSelect';
import { trpc, trpcClient } from '@/trpcClient';
import { checkUserCreateInput, type CreateUserInputType } from '@shelflix/schemas/user/create';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Icon from '@shelflix/ui-web/components/Icon';
import { toast } from 'sonner';
import { useUserFormStore } from './store';
import { logger } from '@/utils/logger';

export function UserCreate() {
  const { formData, resetForm } = useUserFormStore();
  const createUserMutation = useMutation(trpc.user.create.mutationOptions());
  const navigate = useNavigate();

  const form = useForm<
    CreateUserInputType & {
      active?: boolean;
      responsibleUser: {
        uuid: string;
        name: string;
      };
    }
  >({
    defaultValues: {
      name: formData?.name ?? '',
      email: formData?.email ?? '',
      phone: formData?.phone ?? '',
      registration: formData?.registration ?? '',
      role: formData?.role ?? '',
      responsibleUser: formData?.responsibleUser ?? null,
      active: formData?.active ?? true,
    },
    mode: 'onSubmit',
  });

  const { data: roles = [] } = useQuery(
    trpc.role.list.queryOptions({ sort: { field: 'hierarchy', direction: 'asc' } })
  );

  const currentRole = form.watch('role');

  // Real-time validation for required fields
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'phone' || name === 'email' || name === 'registration') {
        const hasPhone = value.phone && value.phone.replaceAll(/[^\d]/g, '').length >= 10;
        const hasEmail = value.email && value.email.trim().length > 0;
        const hasRegistration = value.registration && value.registration.trim().length > 0;

        if (!hasPhone && !hasEmail && !hasRegistration) {
          form.setError('phone', {
            type: 'manual',
            message: 'É obrigatório informar pelo menos um dos campos: matrícula, telefone ou e-mail',
          });
          form.setError('email', {
            type: 'manual',
            message: 'É obrigatório informar pelo menos um dos campos: matrícula, telefone ou e-mail',
          });
          form.setError('registration', {
            type: 'manual',
            message: 'É obrigatório informar pelo menos um dos campos: matrícula, telefone ou e-mail',
          });
        } else {
          form.clearErrors(['phone', 'email', 'registration']);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const fetchUsers = useCallback(
    async (searchValue: string) => {
      logger.info('🔍 fetchItems called with:', searchValue);
      if (!searchValue || !currentRole) return [];

      // Fetch directly via trpcClient to avoid stale data from useQuery cache
      const result = await trpcClient.user.list.query({
        search: {
          fields: ['name', 'email'],
          value: searchValue,
        },
        filter: [
          { name: 'active', condition: 'equals', value: true },
          { name: 'hierarchy', condition: 'lt', value: roles.find((r) => r.uuid === currentRole)?.hierarchy ?? 0 },
        ],
      });

      logger.info('🔍 query result:', result.users);
      logger.info('🔍 total users returned:', result.users?.length ?? 0);
      return result.users ?? [];
    },
    [currentRole, roles]
  );

  const onSubmit = (
    data: CreateUserInputType & {
      active?: boolean;
      responsibleUser: {
        uuid: string;
        name: string;
      };
    }
  ) => {
    if (createUserMutation.isPending) return;
    const payload = {
      email: data.email,
      name: data.name,
      phone: data.phone?.replaceAll(/[^\d]/g, '') || undefined,
      registration: data.registration,
      responsibleUser:
        data.role !== roles.find((r) => r.hierarchy === Math.min(...roles.map((r) => r.hierarchy)))?.uuid
          ? (data.responsibleUser.uuid ?? data.responsibleUser)
          : '00000000-0000-4000-8000-000000000000', //FIXME setting valid uuid for admin to avoid validation error
      role: data.role,
      active: !!data.active,
    };

    createUserMutation.mutate(
      { ...payload, role: payload.role },
      {
        onSuccess: async () => {
          await new Promise((r) => setTimeout(r, 300));

          navigate('/user', { state: { toastSuccess: true } });
          resetForm();
        },
        onError: (error) => {
          toast.error(`Erro ao cadastrar usuário: ${error.message}`);
        },
      }
    );
  };

  return (
    <BaseLayout>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, scrollToError)} className="flex w-full flex-col gap-4">
          <Card className="rounded-[10px]">
            <CardHeader className="pb-0">
              <CardTitle className="text-primary text-[18px] leading-5 font-semibold sm:text-2xl">
                Informações pessoais
              </CardTitle>
              <p className="mt-2 text-sm text-gray-600">
                É obrigatório informar pelo menos um dos campos: matrícula, telefone ou e-mail
              </p>
            </CardHeader>
            <CardContent className="grid gap-2 sm:grid-cols-2 sm:gap-[27px_12px] xl:grid-cols-4">
              <FormField
                control={form.control}
                name="name"
                rules={{ validate: (value) => checkUserCreateInput('name', value) }}
                render={({ field }) => (
                  <FormItem className="col-span-3 flex flex-col gap-[3px] sm:col-span-1">
                    <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Nome e sobrenome</FormLabel>
                    <FormControl>
                      <Input className="h-10" placeholder="Digite o nome do usuário" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="registration"
                rules={{ validate: (value) => checkUserCreateInput('registration', value) }}
                render={({ field }) => (
                  <FormItem className="col-span-3 flex flex-col gap-[3px] sm:col-span-1">
                    <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Matrícula</FormLabel>
                    <FormControl>
                      <Input className="h-10" placeholder="Digite a matrícula" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                rules={{ validate: (value) => checkUserCreateInput('phone', value?.replaceAll(/[^\d]/g, '')) }}
                render={({ field }) => (
                  <FormItem className="col-span-3 flex flex-col gap-[3px] sm:col-span-1">
                    <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Número de WhatsApp</FormLabel>
                    <FormControl>
                      <Input className="h-10" mask="(99) 99999-9999" placeholder="(__) _____-____" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                rules={{ validate: (value) => checkUserCreateInput('email', value) }}
                render={({ field }) => (
                  <FormItem className="col-span-3 flex flex-col gap-[3px] sm:col-span-1">
                    <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">E-mail</FormLabel>
                    <FormControl>
                      <Input className="h-10" placeholder="Digite o e-mail corporativo" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card className="rounded-[10px]">
            <CardHeader className="pb-0">
              <CardTitle className="text-primary text-[18px] leading-5 font-semibold sm:text-2xl">
                Controle de acesso e visualização
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-8 sm:flex-row">
              <FormField
                control={form.control}
                name="role"
                rules={{ validate: (value) => checkUserCreateInput('role', value) }}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-[3px] sm:w-sm">
                    <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Tipo de acesso</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={(uuid) => {
                          const selected = roles.find((r) => r.uuid === uuid);
                          if (selected) {
                            field.onChange(selected.uuid);
                          }
                        }}
                        defaultValue={field.value ?? undefined}
                        value={field.value ?? undefined}
                      >
                        <SelectTrigger className="h-10 w-full">
                          <SelectValue placeholder="Selecione o tipo de acesso" />
                        </SelectTrigger>
                        <SelectContent className=" ">
                          {roles.map((role) => (
                            <SelectItem key={role.uuid} value={role.uuid}>
                              {role.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="active"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2.5">
                    <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Status</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={(value) => field.onChange(value === 'true')}
                        defaultValue={String(field.value)}
                        className="flex gap-6"
                        value={String(field.value)}
                        disabled={true}
                      >
                        <FormItem className="flex items-center space-y-0 space-x-2">
                          <FormControl>
                            <RadioGroupItem value={'true'} id="ativo" />
                          </FormControl>
                          <FormLabel
                            htmlFor="ativo"
                            className={
                              field.value === true ? 'text-primary text-sm font-semibold' : 'text-sm text-[#6d6d6d]'
                            }
                          >
                            Ativo
                          </FormLabel>
                        </FormItem>
                        <FormItem className="flex items-center space-y-0 space-x-2">
                          <FormControl>
                            <RadioGroupItem value="false" id="inativo" />
                          </FormControl>
                          <FormLabel
                            htmlFor="inativo"
                            className={
                              field.value === false ? 'text-primary text-sm font-semibold' : 'text-sm text-[#6d6d6d]'
                            }
                          >
                            Inativo
                          </FormLabel>
                        </FormItem>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {!!currentRole &&
            roles.find((r) => r.uuid === currentRole)?.hierarchy !== Math.min(...roles.map((r) => r.hierarchy)) && (
              <Card className="rounded-[5px]">
                <CardContent>
                  <div className="mb-6">
                    <CardTitle className="text-primary mb-2 [font-family:'Poppins'] text-[18px] leading-5 font-semibold sm:text-2xl">
                      Responsável direto
                    </CardTitle>
                    <p className="mt-4 [font-family:'Figtree'] text-sm leading-5 text-[#2d2d2d]">
                      Todo usuário deverá ter um responsável direto, de cargo superior. <br />
                      Caso não haja um cargo acima deste usuário cadastrado, informe abaixo o e-mail do administrador.
                    </p>
                  </div>

                  <FormField
                    control={form.control}
                    name="responsibleUser"
                    rules={{ validate: (value) => checkUserCreateInput('responsibleUser', value?.uuid ?? value) }}
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-[3px] sm:w-[391px]">
                        <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                          Pesquisar usuário
                        </FormLabel>
                        <FormControl>
                          <div className="flex flex-row items-center gap-2">
                            <GenericSelect<UserDto>
                              disabled={!currentRole}
                              onSelect={(user) => field.onChange(user)}
                              placeholder="Digite o nome para buscar"
                              value={field.value}
                              fetchItems={fetchUsers}
                              getItemKey={(user) => user.uuid}
                              renderItem={(user) => (
                                <div>
                                  <div className="text-sm font-medium">{user.name}</div>
                                  <div className="text-muted-foreground text-xs">{user.email}</div>
                                </div>
                              )}
                              getItemLabel={(user) => user.name}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            )}

          <div className="my-5 flex grow-0 justify-center">
            <Button
              type="submit"
              loading={createUserMutation.isPending}
              className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold text-white sm:w-auto sm:w-fit sm:grow-0"
            >
              <Icon name="person" color="#fff" />
              <span className="[font-family:'Figtree'] text-sm">Salvar usuário</span>
            </Button>
          </div>
        </form>
      </Form>
    </BaseLayout>
  );
}
